import { CategoryEntity, CategoryGroupEntity } from 'loot-core/types/models';
import { AggregateMode, DimensionMode } from './shared';
import { integerToAmount } from 'loot-core/shared/util';
import { getRandomChartColor } from '../util';

interface IteratorReturnData {
  monthly: number;
  yearly: number;
}

export async function getSpendingDistributionData(
  categoryGroups: CategoryGroupEntity[],
  categories: CategoryEntity[],
  dimensionMode: DimensionMode,
  aggregateMode: AggregateMode,
  getCategoryGroupData: (catGroup: CategoryGroupEntity) => Promise<IteratorReturnData>,
  getCategoryData: (cat: CategoryEntity) => Promise<IteratorReturnData>
) {
  let spendingDistribution = [];
  const maxLabelCount = 10;

  if (dimensionMode === DimensionMode.CategoryGroup) {
    for (const catGroup of categoryGroups) {
      const value = await getCategoryGroupData(catGroup);
      if (!catGroup.is_income && value && value.yearly) {
        spendingDistribution.push({
          value: value,
          name: catGroup.name,
          color: getRandomChartColor(catGroup.name),
        });
      }
    }
  } else {
    for (const cat of categories) {
      const value = await getCategoryData(cat);
      if (!cat.is_income && value && value.yearly) {
        spendingDistribution.push({
          value: value,
          name: cat.name,
          color: getRandomChartColor(cat.name),
        });
      }
    }
  }

  spendingDistribution.sort((a, b) => {
    return b.value.monthly - a.value.monthly;
  });

  if (spendingDistribution.length > maxLabelCount) {
    const otherDistributions = spendingDistribution.splice(maxLabelCount - 1);

    const otherDistributionAmount = otherDistributions.reduce(
      (previousValue, currentValue) =>
        previousValue + currentValue.value.monthly,
      0,
    );
    spendingDistribution.push({
      value: {
        monthly: otherDistributionAmount,
        yearly: otherDistributionAmount * 12,
      },
      name: 'Other',
      color: getRandomChartColor('Other'),
    });
  }

  spendingDistribution = spendingDistribution
    .filter(o => o && o.value)
    .map(o => {
      const value = aggregateMode === AggregateMode.MonthlyAverage ? o.value.monthly : o.value.yearly;
      return {
        ...o,
        value,
      };
    });
  const total = spendingDistribution.reduce((acc, o) => acc + o.value, 0);
  spendingDistribution = spendingDistribution.map(o => ({
    ...o,
    value: integerToAmount(o.value),
    percentage: o.value / total,
  }));

  return spendingDistribution;
}

export async function getCashflowProjectionData(
  categoryGroups: CategoryGroupEntity[],
  aggregateMode: AggregateMode,
  getGroupData: (catGroups: CategoryGroupEntity[]) => Promise<IteratorReturnData>
) {
  const incomeGroups = categoryGroups.filter(o => o.is_income);
  const donationGroups = categoryGroups.filter(o => o.is_donation);
  const expenseGroups = categoryGroups.filter(o => !o.is_income && !o.is_donation);

  const totals = {
    income: await getGroupData(incomeGroups),
    donations: await getGroupData(donationGroups),
    expenses: await getGroupData(expenseGroups),
    balance: {
      monthly: 0,
      yearly: 0
    }
  };
  totals.balance.monthly = totals.income.monthly - totals.expenses.monthly - totals.donations.monthly;
  totals.balance.yearly = totals.income.yearly - totals.expenses.yearly - totals.donations.yearly;

  const aggregateKey = aggregateMode === AggregateMode.MonthlyAverage ? 'monthly' : 'yearly';
  const data = [
    {
      name: 'Net',
      color: '#3c9711',
      value: integerToAmount(totals.balance[aggregateKey]),
    },
    {
      name: 'Generosity',
      color: '#50b0ed',
      value: integerToAmount(totals.donations[aggregateKey]),
    },
    {
      name: 'Other expenses',
      color: '#f5c02e',
      value: integerToAmount(totals.expenses[aggregateKey]),
    },
    {
      name: 'Income',
      color: '#9897fc',
      value: integerToAmount(totals.income[aggregateKey]),
    },
  ];

  return data;
}
